import { request } from "@/axios";
import { rejectIfNotLogin } from "@/utils/requireLogin";
import { type Module } from "@/types/artwork/generate";
import { type LocalUploadImageState } from "@/components/ImageSelect/service";
import { type ListCommonParams, type ListOffsetParams, type ListResponse } from "@/types/list";
import type { ModelDetailsInfo } from "@/types/model";
import type { ArtworkVersionImage } from "@/types/image";
import { encrypt, useAES } from "@/utils/AES";
import { jsonParse } from "@/utils";
import { message } from "ant-design-vue";
import { useUserStore } from "@/store/user";
import { ModuleId, type ModuleIds } from "@xmiles/holopix-types";

// 创建作品
interface CreatArtwork {
  artworkName: string;
  draftParam?: string | null;
  modelList: string;
  lastModule: ModuleIds;
  hide?: boolean;
  /** 4 为调试作品，不进入普通资产列表。 */
  artworkType?: number;
}
export function creatArtwork(data: CreatArtwork) {
  return request<number>({
    api: "/api/artwork/add",
    method: "post",
    data,
    notToast: false
  });
}

// 编辑作品
export interface ArtworkState {
  id: number;
  modelList: ModelDetailsInfo[];
  artworkName: string;
  lastModule?: ModuleId;
  shareType?: number; // 1: 团队共享,0: 个人
}
interface EditArtworkData extends Omit<ArtworkState, "modelList"> {
  modelList: string | null;
}
export function editArtwork(data: EditArtworkData) {
  return request<any>({
    api: "/api/artwork/edit",
    method: "post",
    data,
    notToast: false
  });
}

// 删除作品
interface DeleteArtwork {
  id: number;
}
export function deleteArtwork(data: DeleteArtwork) {
  return request<any>({
    api: "/api/artwork/delete",
    method: "post",
    data,
    notToast: false
  });
}

// 获取作品列表
interface QueryArtworkList extends ListOffsetParams {
  artworkName?: string;
  orderCondition: number; // 1: 创建时间 2: 更新时间
  shareType?: number; // 新增参数：团队版个人共享标识 0非共享 1共享
  /** 用户id */
  userId?: number;
  /** 团队id */
  teamId?: number;
}
export interface ArtworkItem {
  artworkName: string;
  ctime: string;
  id: number;
  preview: string | null;
  utime: string;
  modelList: string | null;
  avatar: string; // 头像
  nickname: string; // 昵称
  // 用户上传的原图
  originImgUrl: string;
}
export function queryArtworkList(data: QueryArtworkList) {
  return request<ListResponse<ArtworkItem[]>>({
    api: "/api/artwork/list",
    method: "post",
    data,
    notToast: false
  });
}

// 获取作品版本列表
interface QueryArtworkVersionList extends ListCommonParams {
  artworkId?: number;
  module?: number | null;
  moduleList?: number[];
  source?: number | null;
}
export interface ArtworkVersion {
  artworkId: number;
  clientId: string;
  ctime: string;
  finishTime: string;
  id: number;
  images: ArtworkVersionImage[];
  module: Module;
  param: string;
  promptId: string;
  referId: number;
  source: number;
  status: number;
  utime: string;
  nickname: string;
  shareType: number;
  videoUrl?: string;
}
export function queryArtworkVersionList(data: QueryArtworkVersionList) {
  return request<ListResponse<ArtworkVersion[] | null>>({
    api: "/api/artwork/versionList",
    method: "post",
    data,
    notToast: false
  });
}

// 获取作品参考图列表
interface QueryArtworkReferList extends ListCommonParams {
  artworkId: number;
  module: number | null;
  source?: number | null;
}
export interface ArtworkReferItem {
  id: number;
  collect: 0 | 1;
  image: string;
  width: number;
  height: number;
  param: string | null;
}
export function queryArtworkReferList(data: QueryArtworkReferList) {
  return request<ListResponse<ArtworkReferItem[] | null>>({
    api: "/api/artwork/referList",
    method: "post",
    data,
    notToast: false
  });
}

// 获取作品每个创作模块最新版本
interface QueryArtworkModuleVersion {
  artworkId: number;
}
interface ModuleVersionBase {
  id: number;
  artworkId: number;
  clientId: string;
  promptId: string;
  module: Module;
  status: number;
  source: number;
  param: string;
  referId: number;
  styleReferId: string;
  finishTime: string;
  ctime: string;
  utime: string;
  images: ArtworkVersionImage[];
}
interface QueryArtworkModuleVersionResponse {
  id: number;
  artworkName: string;
  modelList: string | null;
  lastModule: ModuleId;
  module: Record<Module, ModuleVersionBase>;
  draftParam: string | null;
}
export function queryArtworkModuleVersion(data: QueryArtworkModuleVersion) {
  return request<QueryArtworkModuleVersionResponse | null>({
    api: "/api/artwork/moduleVersion",
    method: "post",
    data,
    notToast: false
  });
}

// 获取预设姿态图
export function queryPose() {
  return request<LocalUploadImageState[]>({
    api: "/api/localUpload/getPose",
    method: "get",
    notToast: false
  });
}

// 获取图片详情
interface QueryImageDetail {
  id: number;
}
interface ImageDetailState {
  artworkId: number;
  artworkName: string;
  collect: 0 | 1;
  id: number;
  image: string;
  width: number;
  height: number;
  param?: string | null;
  versionId: number;
  type?: number;
}
export function queryImageDetail(data: QueryImageDetail) {
  return request<ImageDetailState>({
    api: "/api/artwork/imgDetail",
    method: "post",
    data,
    notToast: false
  });
}

// 删除姿势
interface DelPose {
  id: number;
}
export function delPose(data: DelPose) {
  return request({
    api: "/api/localUpload/delPose",
    method: "post",
    data,
    notToast: false
  });
}

// 获取姿势信息
interface QueryPoseInfo {
  poseId: number;
}
export function queryPoseInfo(params: QueryPoseInfo) {
  return request<string>({
    api: "/api/localUpload/poseInfo",
    method: "get",
    params,
    notToast: false
  });
}

export interface PoseState {
  width: number;
  height: number;
  image: string;
  size: number;
  info: string;
}
// 上传姿势图
export function uploadPose(data: PoseState) {
  return request({
    api: "/api/localUpload/uploadPose",
    method: "post",
    data,
    notToast: false
  });
}

// 编辑姿势图
interface EditPose extends PoseState {
  id: number;
}
export function editPose(data: EditPose) {
  return request({
    api: "/api/localUpload/editPose",
    method: "post",
    data,
    notToast: false
  });
}

// 智能编辑器手动添加版本
interface ReplenishVersionParams {
  clientId: string;
  artworkId: number;
  module: number;
  subModule: number;
  source: number;
  referId: number;
  image: string;
  width: number;
  height: number;
  size: number;
  param?: string;
}
export function replenishVersion(data: ReplenishVersionParams) {
  return request<number>({
    api: "/api/common/replenishVersion",
    method: "post",
    data,
    notToast: false
  });
}

// 取消生图任务
export function cancelGenerate(data: { clientId: string; module: Module }) {
  return request({
    api: "/api/task/cancel",
    method: "post",
    data,
    notToast: true
  });
}

// 立即插队
export function jumpQueueGenerate(data: { clientId: string; module: Module }) {
  return request({
    api: "/api/task/cancelAndSpeed",
    method: "post",
    data
  });
}

// 新用户首次生图
export function newUserFirstGenerate(data: { clientId: string; artworkId: string; param: string; abTest?: string; modelId?: number }) {
  return request({
    api: "/api/artwork/newUserFirstGenerate",
    method: "post",
    data
  });
}

// 获取随机提示词
interface RandomPromptResponse {
  promptList: string[];
  titlePrompt: string[];
}

export function queryRandomPrompt(data: { id: number }) {
  return request<RandomPromptResponse>({
    api: "/api/model/getRandomTitlePrompt",
    method: "post",
    data
  });
}

// 深度用户
const newUserGuidImages = [
  "https://pino-img.yingzhongshare.com/new_user_first_gene/img1.png",
  "https://pino-img.yingzhongshare.com/new_user_first_gene/img2.png"
];
export function deepUser(url: string) {
  if (newUserGuidImages.includes(url)) {
    return false;
  }

  return request({
    api: "/api/conv",
    method: "post",
    data: {
      eventCode: 9
    }
  });
}

// 批量删除版本图片、版本
export function batchDelVersion(data: { imgIdList: number[]; versionIdList: number[] }) {
  return request<string[]>({
    api: "/api/artwork/batchDelVersion",
    method: "post",
    data
  });
}

// 批量收藏版本图片
export function batchCollect(data: { imgIdList: number[] }) {
  const loginRejected = rejectIfNotLogin();
  if (loginRejected) return loginRejected;

  return request<string[]>({
    api: "/api/artwork/batchCollect",
    method: "post",
    data
  });
}

// 智能提示词、姿态识别
interface ImprovePrompt {
  image?: string;
  modelId: number;
  prompt?: string;
  requestType?: number; // 1-图片反推提示词，2-图片反推姿势
  planId?: number; // 方案
  userId?: string;
}
export async function improvePrompt(
  data: ImprovePrompt,
  init: (controller: AbortController) => void,
  successCallback: (value: string) => void,
  errorCallback: () => void
) {
  // 统一添加userId
  const userStore = useUserStore();
  data.userId = userStore.userInfo.id;

  // 创建 AbortController 实例
  const controller = new AbortController();
  init(controller);

  const decoder = new TextDecoder("utf-8");
  let isCompleted = false;

  // 检查是否为错误响应
  const isErrorData = (parsedData: any) => parsedData?.status === -1 || parsedData?.code === 80003 || parsedData?.success === false;

  // 处理单个消息
  const processMessage = (messageItem: string) => {
    let parsedData = null;

    if (messageItem.startsWith("data:")) {
      const jsonStr = messageItem.slice(5).trim();
      parsedData = jsonStr ? jsonParse(jsonStr) : null;
    } else {
      parsedData = jsonParse(messageItem);
    }

    if (!parsedData) return;

    if (isErrorData(parsedData)) {
      message.error(parsedData?.msg || parsedData?.content || "操作失败");
      isCompleted = true;
      errorCallback();
      return;
    }

    // 检查完成状态
    if (parsedData.status === 3) {
      isCompleted = true;
    }

    successCallback(parsedData);
  };

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    if (!useAES) {
      headers["xmReqRaw"] = "1";
      headers["xmRespRaw"] = "1";
    }

    const response = await fetch(`${import.meta.env.VITE_SERVICE_URL}/aiApi/improvePrompt`, {
      method: "POST",
      headers,
      body: useAES ? encrypt({ data }) : JSON.stringify({ data }),
      signal: controller.signal
    });

    if (!response.ok) {
      throw new Error(`请求失败: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("响应流不可读");
    }

    // 流式读取数据
    while (!isCompleted) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const messages = chunk.split("\n");

      messages.forEach(processMessage);

      // 任务完成后主动关闭流
      if (isCompleted) {
        try {
          await reader.cancel();
        } catch {
          // 忽略取消错误
        }
      }
    }
  } catch (error: any) {
    // 处理取消操作
    if (error?.message?.includes("signal is aborted") || error?.message?.includes("AbortError")) {
      message.success("取消成功");
    } else if (!error?.message?.includes("aborted")) {
      message.error(error?.message || "操作失败");
    }
    errorCallback();
  }
}

// 获取方案下拉弹窗
type QueryPlanOptionsResult = {
  id: number;
  name: string;
}[];
export function queryPlanOptions() {
  return request<QueryPlanOptionsResult>({
    api: "/api/model/planItems",
    method: "get"
  });
}

// 共享作品
interface ShareArtworkParams {
  artworkId: number; // 作品id
  versionId: number; // 作品版本id
}
export function shareArtwork(data: ShareArtworkParams) {
  return request({
    api: "/api/artwork/shareArtwork",
    method: "post",
    data,
    notToast: false
  });
}

// 去除背景
interface RemoveBackgroundParams {
  userId: string;
  module: 10;
  threeDOriginImgUrl: string;
  requestId?: string;
  threeDClientId?: string;
}
export function removeBackground(data: RemoveBackgroundParams) {
  return request({
    api: "/aiApi/gene3d",
    method: "post",
    data: {
      message: JSON.stringify(data)
    },
    notToast: false
  });
}

// 生成3D
interface Generate3DParams {
  userId: string;
  module: 11;
  threeDOriginImgUrl: string;
  teamId: number | "";
  threeDSimplify?: number; // (0-1之间 2位小数支持)
  requestId?: string;
  threeDClientId?: string;
  /** 去掉背景后的图片 */
  threeDRmbgImg?: string;
  artworkId?: number;
  source?: number;
}
export function generate3D(data: Generate3DParams) {
  return request({
    api: "/aiApi/gene3d",
    method: "post",
    data: {
      message: JSON.stringify(data)
    },
    notToast: false
  });
}

// 收藏3D模型
interface Collect3DParams {
  glbId: number;
  collect: 0 | 1; // 0:取消收藏 1:收藏
}
export function collect3D(data: Collect3DParams) {
  const loginRejected = rejectIfNotLogin();
  if (loginRejected) return loginRejected;

  return request({
    api: "/api/collect/collectGlb",
    method: "post",
    data,
    notToast: false
  });
}

// 获取3D收藏列表
interface Query3DCollectionList extends ListOffsetParams {
  userId: string;
  teamId: number | string;
}
export function query3DCollectionList(data: Query3DCollectionList) {
  return request<ListResponse<any[]>>({
    api: "/api/collect/glbList",
    method: "post",
    data,
    notToast: false
  });
}

// 人脸检测
export function faceDetection(data: any) {
  return request({
    api: "/aiApi/faceDetection",
    method: "post",
    data: {
      message: JSON.stringify(data)
    },
    notToast: false
  });
}

/**
 * 超分
 * 用于将图片放大
 */
interface SuperImgParams {
  module: number;
  originImgUrl: string;
  apiClientId: string;
  /** 放大倍数 */
  outscale: number;
}

export function superImg(data: SuperImgParams) {
  let api;

  switch (data.module) {
    case 21:
      api = "/aiApi/superAndRmImg";
      break;
    case 10:
      api = "/aiApi/removeBgImg";
      break;
    default:
      api = "/aiApi/superImg"; // 9
      break;
  }

  return request({
    api,
    method: "post",
    data: {
      message: JSON.stringify(data)
    },
    notToast: false
  });
}

/**
 * 全图分割
 * 用于将图片进行全图分割处理
 */
interface CutoutPointsParams {
  /** 图片URL */
  originImgUrl: string;
  /** 分割方法,固定为'full' */
  segmentMethod: string;
  /** 模块ID */
  module: number;
  /** 用户ID */
  userId: string;
  /** API客户端ID */
  apiClientId: string;
}

export function cutoutPoints(data: CutoutPointsParams) {
  return request({
    api: "/aiApi/cutoutPoints",
    method: "post",
    data: {
      message: JSON.stringify(data)
    },
    notToast: false
  });
}

// 批量下载图片
export function batchDownLoadImage(data: { imgLists: string[] }) {
  return request<string>({
    api: "/api/artwork/download",
    method: "post",
    data,
    notToast: false
  });
}

// 生图记录下载
export interface GeneDownloadParams {
  artworkVersionId?: number;
  clientId?: string;
  geneId?: number;
  type?: number;
}

export function geneDownload(data: GeneDownloadParams) {
  return request({
    api: "/api/artwork/geneDownload",
    method: "post",
    data,
    notToast: true
  });
}

/**
 * 一键成稿/图片推词：确认替换时扣除 1 算力
 * POST /aiApi/api/conceptExplorePrompt，无请求体；token 由 request 注入
 */
export function conceptExplorePrompt() {
  return request({
    api: "/aiApi/api/conceptExplorePrompt",
    method: "post",
    notToast: false
  });
}
